import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  Users,
  Target,
  CheckCircle,
  Award,
  BarChart2,
  Handshake,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';

const Affiliates: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    website: '',
    promotionalChannels: '',
    audienceSize: '',
    experience: '',
    whyJoin: '',
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase.from('affiliate_applications').insert({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone || null,
        website: formData.website || null,
        promotional_channels: formData.promotionalChannels,
        audience_size: formData.audienceSize || null,
        experience: formData.experience || null,
        why_join: formData.whyJoin,
      });

      if (error) {
        if (error.code === '23505') {
          toast.error('An application with this email already exists');
        } else {
          throw error;
        }
        return;
      }

      toast.success('Application submitted successfully! We will review it and get back to you soon.');

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        website: '',
        promotionalChannels: '',
        audienceSize: '',
        experience: '',
        whyJoin: '',
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <section className="gradient-bg text-white py-20 md:py-32">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <Award className="w-5 h-5 mr-2" />
              <span className="font-semibold text-lg">Earn up to 20% recurring commissions</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Join the FutureLens Pro Affiliate Program
            </h1>

            <p className="text-xl md:text-2xl opacity-90 mb-8 max-w-3xl mx-auto">
              Earn generous commissions while helping professionals transform their careers with AI-powered insights
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#apply">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 w-full sm:w-auto">
                  Apply Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="#commission-structure">
                <Button
                  size="lg"
                  variant="secondary"
                  className="w-full sm:w-auto border-white text-white hover:bg-white/10"
                >
                  View Commission Structure
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="commission-structure" className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Transparent Commission Structure</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We believe in rewarding our partners generously. Earn lifetime recurring commissions on every subscription you refer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-blue-500 hover:shadow-xl transition-shadow">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4 mx-auto">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Basic Plan</h3>
                <div className="text-4xl font-bold text-blue-600 mb-2">15%</div>
                <p className="text-gray-600">Recurring Commission</p>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">$0/month subscription tier</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">One-time setup fee commission</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Lifetime recurring payouts</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-teal-500 hover:shadow-xl transition-shadow transform md:scale-105">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <span className="bg-gradient-to-r from-blue-600 to-teal-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                  MOST POPULAR
                </span>
              </div>
              <div className="text-center mb-6 mt-4">
                <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mb-4 mx-auto">
                  <TrendingUp className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Professional Plan</h3>
                <div className="text-4xl font-bold text-teal-600 mb-2">20%</div>
                <p className="text-gray-600">Recurring Commission</p>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">$29/month subscription tier</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Higher commission rate</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Lifetime recurring payouts</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-purple-500 hover:shadow-xl transition-shadow">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4 mx-auto">
                  <Award className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Enterprise Plan</h3>
                <div className="text-4xl font-bold text-purple-600 mb-2">20%+</div>
                <p className="text-gray-600">Plus Performance Bonuses</p>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">$45/month subscription tier</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Performance bonuses available</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Lifetime recurring payouts</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-xl p-8 text-white max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-90" />
                <h4 className="font-bold text-lg mb-2">Monthly Payouts</h4>
                <p className="text-sm opacity-90">Via PayPal or Stripe</p>
              </div>
              <div>
                <BarChart2 className="w-10 h-10 mx-auto mb-3 opacity-90" />
                <h4 className="font-bold text-lg mb-2">60-Day Cookie</h4>
                <p className="text-sm opacity-90">Extended tracking window</p>
              </div>
              <div>
                <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-90" />
                <h4 className="font-bold text-lg mb-2">Lifetime Recurring</h4>
                <p className="text-sm opacity-90">As long as they subscribe</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Join Our Affiliate Program?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Partner with a growing AI-powered career platform and earn generous commissions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-6 mx-auto">
                <BarChart2 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">High-Converting Product</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>7:1 LTV:CAC ratio demonstrates strong market fit</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Proven product-market fit with satisfied customers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>AI-powered career solution in rapidly growing market</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-6 mx-auto">
                <DollarSign className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Generous Rewards</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Up to 20% recurring commission on subscriptions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Performance bonuses for top affiliates</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Monthly reliable payouts via PayPal or Stripe</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-6 mx-auto">
                <Handshake className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Complete Support</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Dedicated affiliate manager for personalized support</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Comprehensive marketing materials and assets</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Regular training and performance optimization</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="apply" className="py-20 bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Apply to Join Today</h2>
              <p className="text-lg text-gray-600">
                Fill out the form below and we'll review your application within 2-3 business days
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="John"
                  />
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                  Website or Social Media URL
                </label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://yourwebsite.com"
                />
              </div>

              <div>
                <label htmlFor="promotionalChannels" className="block text-sm font-medium text-gray-700 mb-2">
                  How do you plan to promote FutureLens Pro? *
                </label>
                <textarea
                  id="promotionalChannels"
                  name="promotionalChannels"
                  required
                  rows={4}
                  value={formData.promotionalChannels}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Describe your promotional channels (e.g., blog, YouTube, social media, email list, etc.)"
                />
              </div>

              <div>
                <label htmlFor="audienceSize" className="block text-sm font-medium text-gray-700 mb-2">
                  Audience Size / Reach
                </label>
                <select
                  id="audienceSize"
                  name="audienceSize"
                  value={formData.audienceSize}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select an option</option>
                  <option value="0-1000">0 - 1,000</option>
                  <option value="1000-5000">1,000 - 5,000</option>
                  <option value="5000-10000">5,000 - 10,000</option>
                  <option value="10000-50000">10,000 - 50,000</option>
                  <option value="50000+">50,000+</option>
                </select>
              </div>

              <div>
                <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                  Previous Affiliate Marketing Experience
                </label>
                <textarea
                  id="experience"
                  name="experience"
                  rows={3}
                  value={formData.experience}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Tell us about your experience with affiliate marketing (optional)"
                />
              </div>

              <div>
                <label htmlFor="whyJoin" className="block text-sm font-medium text-gray-700 mb-2">
                  Why do you want to join the FutureLens Pro affiliate program? *
                </label>
                <textarea
                  id="whyJoin"
                  name="whyJoin"
                  required
                  rows={4}
                  value={formData.whyJoin}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Share your motivation and how FutureLens Pro aligns with your audience"
                />
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting Application...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </div>

              <p className="text-sm text-gray-600 text-center">
                By submitting this form, you agree to our terms and conditions for the affiliate program.
              </p>
            </form>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container">
          <div className="bg-gradient-to-r from-blue-600 via-teal-600 to-blue-600 rounded-2xl p-12 text-white text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <div className="relative z-10 max-w-3xl mx-auto">
              <Users className="w-16 h-16 mx-auto mb-6 opacity-90" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Start Earning?
              </h2>
              <p className="text-xl opacity-90 mb-8">
                Join successful affiliates who are earning recurring commissions by helping professionals transform their careers.
              </p>
              <a href="#apply">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                  Apply Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <p className="mt-6 text-sm opacity-75">Questions? Contact us at connect@optivusbusinesssolutions.com</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Affiliates;
